import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import { PdfViewer } from "@/components/PdfViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusPill } from "@/components/StatusPill";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MapPin, Building2, Trash2, AlertOctagon,
  StickyNote, Plus, Check, X, Pencil, UserRoundPen, UserRoundMinus, Info, Search, ShieldCheck, CalendarClock,
  FileUp, FileText, Download, FolderOpen, Loader2, Eye,
} from "lucide-react";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

const APPROVAL_LABELS = { not_started: "Not started", pending: "Pending", approved: "Approved", denied: "Denied" };
const APPROVAL_TONE = { not_started: "gray", pending: "orange", approved: "green", denied: "red" };
const noteFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "NP";
}

function NoteCard({ note, onDelete, onSaveEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    await onSaveEdit(note.id, body);
    setEditing(false);
  };
  return (
    <div className="border border-[#E5E0D8] rounded-md p-3 bg-[#FAF8F3]" data-testid={`np-note-${note.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.06em] text-[#8A847A]">
          {note.updated_at ? "Edited " : ""}{noteFmt.format(new Date(note.updated_at || note.created_at))}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!editing && (
            <button onClick={() => { setDraft(note.body); setEditing(true); }} className="text-[#5C5850] hover:text-[#4A6B5D] p-1" aria-label="Edit note" data-testid={`np-note-edit-${note.id}`}>
              <Pencil className="h-3.5 w-3.5"/>
            </button>
          )}
          <button onClick={() => onDelete(note.id)} className="text-[#C17D6E] hover:text-[#8A3E30] p-1" aria-label="Delete note" data-testid={`np-note-delete-${note.id}`}>
            <Trash2 className="h-3.5 w-3.5"/>
          </button>
        </div>
      </div>
      {editing ? (
        <div className="mt-2">
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} className="bg-white border-[#E5E0D8]" data-testid={`np-note-edit-textarea-${note.id}`} />
          <div className="flex justify-end gap-1 mt-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}><X className="h-3.5 w-3.5 mr-1"/> Cancel</Button>
            <Button size="sm" onClick={submit} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid={`np-note-save-edit-${note.id}`}><Check className="h-3.5 w-3.5 mr-1"/> Save</Button>
          </div>
        </div>
      ) : (
        <div className="mt-1.5 text-sm text-[#292723] whitespace-pre-wrap">{note.body}</div>
      )}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function NpDocuments({ npId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null); // docx / xlsx / csv rendered HTML
  const [previewPdfBuffer, setPreviewPdfBuffer] = useState(null); // for pdf.js
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/nps/${npId}/documents`);
      setDocs(data?.documents || []);
    } catch { /* silent */ }
  }, [npId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Clean up blob URL when preview closes
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`${file.name} is over 20MB — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/nps/${npId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? `Uploaded ${files.length} files` : "Document uploaded");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/nps/${npId}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const openPreview = async (d) => {
    setPreviewDoc(d);
    setPreviewLoading(true);
    setPreviewHtml(null);
    setPreviewPdfBuffer(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    try {
      const res = await api.get(`/nps/${npId}/documents/${d.id}/download`, { responseType: "arraybuffer" });
      const buffer = res.data;
      const ct = d.content_type || "";
      const name = (d.filename || "").toLowerCase();
      const isPdf  = ct === "application/pdf" || name.endsWith(".pdf");
      const isDocx = ct.includes("wordprocessingml") || name.endsWith(".docx");
      const isXlsx = ct.includes("spreadsheetml") || ct.includes("excel") || name.endsWith(".xlsx") || name.endsWith(".xls");
      const isCsv  = ct.includes("csv") || name.endsWith(".csv");

      if (isPdf) {
        // Keep a copy for our PdfViewer; also make a blob URL so "download" shortcut works.
        setPreviewPdfBuffer(buffer);
        const blob = new Blob([buffer], { type: "application/pdf" });
        setPreviewUrl(URL.createObjectURL(blob));
      } else if (isDocx) {
        const { value } = await mammoth.convertToHtml({ arrayBuffer: buffer });
        setPreviewHtml(value || "<p>(empty document)</p>");
      } else if (isXlsx) {
        const wb = XLSX.read(buffer, { type: "array" });
        const parts = wb.SheetNames.map((sn) => {
          const html = XLSX.utils.sheet_to_html(wb.Sheets[sn], { header: `<h3 class="text-sm font-medium text-[#4A6B5D] mt-4 mb-2">${sn}</h3>` });
          return html;
        });
        setPreviewHtml(parts.join(""));
      } else if (isCsv) {
        const text = new TextDecoder("utf-8").decode(buffer);
        const wb = XLSX.read(text, { type: "string" });
        const sn = wb.SheetNames[0];
        setPreviewHtml(XLSX.utils.sheet_to_html(wb.Sheets[sn]));
      } else {
        const blob = new Blob([buffer], { type: ct || "application/octet-stream" });
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } catch (e) {
      toast.error("Could not open preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewHtml(null);
    setPreviewPdfBuffer(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const isPreviewable = (ct, filename) => {
    const name = (filename || "").toLowerCase();
    if (!ct && !name) return false;
    if ((ct || "").startsWith("image/")) return true;
    if (ct === "application/pdf" || name.endsWith(".pdf")) return true;
    if ((ct || "").startsWith("text/") || name.endsWith(".txt")) return true;
    if ((ct || "").includes("wordprocessingml") || name.endsWith(".docx")) return true;
    if ((ct || "").includes("spreadsheetml") || (ct || "").includes("excel") || name.endsWith(".xlsx") || name.endsWith(".xls")) return true;
    if ((ct || "").includes("csv") || name.endsWith(".csv")) return true;
    return false;
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    try {
      await api.delete(`/nps/${npId}/documents/${d.id}`);
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const iconFor = (ct) => {
    if (!ct) return FileText;
    if (ct.includes("pdf")) return FileText;
    if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return FileText;
    if (ct.startsWith("image/")) return FileText;
    return FileText;
  };
  const fmtSize = (n) => (!n ? "—" : n < 1024 ? `${n}B` : n < 1024*1024 ? `${(n/1024).toFixed(1)}KB` : `${(n/1048576).toFixed(1)}MB`);

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="np-documents-card">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
          <FolderOpen className="h-3.5 w-3.5"/> Documents
        </div>
        <div className="text-[11px] text-[#8A847A]">{docs.length} {docs.length === 1 ? "file" : "files"}</div>
      </div>

      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadFiles(Array.from(e.dataTransfer.files || []));
        }}
        className={`mt-3 block border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#4A6B5D] bg-[#F0EBE1]" : "border-[#E5E0D8] bg-[#FAF8F3] hover:bg-[#F0EBE1]"
        }`}
        data-testid="np-doc-dropzone"
      >
        <input
          type="file"
          multiple
          hidden
          onChange={(e) => { uploadFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
          disabled={uploading}
          data-testid="np-doc-file-input"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-[#5C5850]">
            <Loader2 className="h-4 w-4 animate-spin"/> Uploading…
          </div>
        ) : (
          <>
            <FileUp className="h-6 w-6 mx-auto text-[#4A6B5D]"/>
            <div className="text-sm text-[#292723] mt-2">Drop a file, or click to browse</div>
            <div className="text-[11px] text-[#8A847A] mt-1">Excel, PDF, images, docs — up to 20MB. Export from Google Drive → upload here.</div>
          </>
        )}
      </label>

      {docs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="np-doc-list">
          {docs.map((d) => {
            const Icon = iconFor(d.content_type);
            const canPreview = isPreviewable(d.content_type, d.filename);
            return (
              <div key={d.id} className="flex items-center gap-3 border border-[#E5E0D8] rounded-md p-3 bg-[#FAF8F3]" data-testid={`np-doc-${d.id}`}>
                <Icon className="h-4 w-4 text-[#4A6B5D] flex-shrink-0"/>
                <button
                  onClick={() => canPreview ? openPreview(d) : download(d)}
                  className="flex-1 min-w-0 text-left hover:opacity-70"
                  data-testid={`np-doc-open-${d.id}`}
                  title={canPreview ? "Click to preview" : "Preview not supported — click to download"}
                >
                  <div className="text-sm text-[#292723] truncate">{d.filename}</div>
                  <div className="text-[11px] text-[#8A847A] mt-0.5">
                    {fmtSize(d.size)} · {d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ""}
                  </div>
                </button>
                {canPreview && (
                  <button onClick={() => openPreview(d)} className="text-[#4A6B5D] hover:text-[#3E594D] p-1" aria-label="Preview" data-testid={`np-doc-preview-${d.id}`}>
                    <Eye className="h-4 w-4"/>
                  </button>
                )}
                <button onClick={() => download(d)} className="text-[#4A6B5D] hover:text-[#3E594D] p-1" aria-label="Download" data-testid={`np-doc-download-${d.id}`}>
                  <Download className="h-4 w-4"/>
                </button>
                <button onClick={() => remove(d)} className="text-[#8A847A] hover:text-[#C17D6E] p-1" aria-label="Delete" data-testid={`np-doc-delete-${d.id}`}>
                  <Trash2 className="h-3.5 w-3.5"/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {previewDoc && (
        <div
          className="fixed inset-0 z-50 bg-[#292723]/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="np-doc-preview-modal"
        >
          <div
            className="bg-white rounded-lg w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E0D8]">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.06em] text-[#8A847A]">Preview</div>
                <div className="text-sm font-medium text-[#292723] truncate">{previewDoc.filename}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => download(previewDoc)}
                  className="inline-flex items-center gap-1.5 text-xs text-[#4A6B5D] hover:underline px-2 py-1"
                  data-testid="np-doc-preview-download"
                >
                  <Download className="h-3.5 w-3.5"/> Download
                </button>
                <button
                  onClick={closePreview}
                  className="text-[#8A847A] hover:text-[#292723] p-1"
                  aria-label="Close preview"
                  data-testid="np-doc-preview-close"
                >
                  <X className="h-5 w-5"/>
                </button>
              </div>
            </div>
            <div className="flex-1 bg-[#F7F5F0] overflow-auto flex items-center justify-center">
              {previewLoading ? (
                <div className="text-[#8A847A] flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin"/> Loading…</div>
              ) : previewPdfBuffer ? (
                <PdfViewer arrayBuffer={previewPdfBuffer} onError={() => toast.error("PDF failed to render")}/>
              ) : previewHtml ? (
                <div
                  className="bg-white w-full h-full overflow-auto p-8 prose prose-sm max-w-none [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-[#E5E0D8] [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-[#E5E0D8] [&_th]:px-2 [&_th]:py-1 [&_th]:bg-[#F7F5F0]"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                  data-testid="np-doc-preview-html"
                />
              ) : !previewUrl ? (
                <div className="text-[#8A847A]">No preview available</div>
              ) : previewDoc.content_type?.startsWith("image/") ? (
                <img src={previewUrl} alt={previewDoc.filename} className="max-w-full max-h-full object-contain"/>
              ) : previewDoc.content_type?.startsWith("text/") ? (
                <iframe src={previewUrl} title={previewDoc.filename} className="w-full h-full bg-white border-0"/>
              ) : (
                <div className="text-center p-8">
                  <FileText className="h-12 w-12 mx-auto text-[#8A847A] opacity-60"/>
                  <p className="text-sm text-[#5C5850] mt-3">Preview not available for this file type.</p>
                  <Button onClick={() => download(previewDoc)} className="mt-4 bg-[#4A6B5D] hover:bg-[#3E594D]">
                    <Download className="h-4 w-4 mr-2"/> Download to view
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NPVisits({ npId }) {
  const today = new Date();
  const monthAgo = new Date(today); monthAgo.setDate(monthAgo.getDate() - 30);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [from_, setFrom] = useState(fmt(monthAgo));
  const [to, setTo] = useState(fmt(today));
  const [visits, setVisits] = useState([]);
  const [count, setCount] = useState(0);
  const [cptSummary, setCptSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { np_id: npId, from_, to };
      const [visitsRes, cptRes] = await Promise.all([
        api.get("/visits", { params }),
        api.get(`/nps/${npId}/cpt-summary`, { params: { from_, to } }).catch(() => ({ data: null })),
      ]);
      setVisits(visitsRes.data?.visits || []);
      setCount(visitsRes.data?.count || 0);
      setCptSummary(cptRes.data);
    } catch { toast.error("Could not load visits"); }
    finally { setLoading(false); }
  }, [npId, from_, to]);
  useEffect(() => { load(); }, [load]);

  const byFacility = {};
  visits.forEach((v) => {
    const k = v.facility_name || "Unknown facility";
    byFacility[k] = (byFacility[k] || 0) + 1;
  });

  return (
    <div className="space-y-4" data-testid="np-visits-tab">
      <div className="bg-white border border-[#E5E0D8] rounded-md p-5">
        <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Date range</div>
        <div className="mt-2 grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] text-[#8A847A]">From</Label>
            <Input type="date" value={from_} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-10 bg-white border-[#E5E0D8]" data-testid="np-visits-from"/>
          </div>
          <div>
            <Label className="text-[11px] text-[#8A847A]">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-10 bg-white border-[#E5E0D8]" data-testid="np-visits-to"/>
          </div>
          <div className="flex items-end">
            <div className="w-full bg-[#F0EBE1] border border-[#E5E0D8] rounded-md p-3 text-center">
              <div className="text-[10px] uppercase tracking-[0.08em] text-[#5C5850]">Total visits</div>
              <div className="heading text-2xl font-bold text-[#292723]" data-testid="np-visits-total">{loading ? "…" : count}</div>
            </div>
          </div>
        </div>

        {(() => {
          if (!from_ || !to) return null;
          const days = Math.max(1, Math.round((new Date(to) - new Date(from_)) / (1000*60*60*24)) + 1);
          const perDay = (count / days).toFixed(2);
          const perWeek = (count / (days / 7)).toFixed(1);
          const perMonth = (count / (days / 30)).toFixed(1);
          return (
            <div className="mt-3 grid grid-cols-3 gap-2 text-center" data-testid="np-visits-rate-tiles">
              <div className="border border-[#E5E0D8] rounded-md p-2 bg-[#FAF8F3]">
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Per day</div>
                <div className="font-mono font-semibold text-[#292723] mt-0.5" data-testid="np-per-day">{perDay}</div>
              </div>
              <div className="border border-[#E5E0D8] rounded-md p-2 bg-[#FAF8F3]">
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Per week</div>
                <div className="font-mono font-semibold text-[#292723] mt-0.5" data-testid="np-per-week">{perWeek}</div>
              </div>
              <div className="border border-[#E5E0D8] rounded-md p-2 bg-[#FAF8F3]">
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Per month</div>
                <div className="font-mono font-semibold text-[#292723] mt-0.5" data-testid="np-per-month">{perMonth}</div>
              </div>
            </div>
          );
        })()}

        {Object.keys(byFacility).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {Object.entries(byFacility).map(([name, n]) => (
              <span key={name} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E5E0D8] bg-[#F7F5F0] text-[#5C5850]">
                <span className="font-medium text-[#292723]">{name}</span>
                <span className="font-mono">{n}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {cptSummary && cptSummary.total_units > 0 && (
        <div className="bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="np-cpt-summary-card">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Top CPT codes · {from_} → {to}</div>
            <div className="flex gap-4 text-xs text-[#5C5850]">
              <span><span className="font-mono font-semibold text-[#292723]">{cptSummary.visits_with_codes}</span> visits coded</span>
              <span><span className="font-mono font-semibold text-[#292723]">{cptSummary.total_units}</span> units</span>
              <span>Est. revenue <span className="font-mono font-semibold text-[#3B5C46]">${cptSummary.total_revenue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span></span>
            </div>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {cptSummary.top_codes.slice(0, 9).map((c) => (
              <div key={c.code} className="border border-[#E5E0D8] rounded-md p-2.5 bg-[#FAF8F3] flex items-center justify-between" data-testid={`np-cpt-top-${c.code}`}>
                <div>
                  <div className="font-mono text-sm font-semibold text-[#292723]">{c.code}</div>
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">{c.count} units · ${c.price.toFixed(0)}/unit</div>
                </div>
                <div className="text-sm font-mono font-semibold text-[#3B5C46]">${c.revenue.toLocaleString(undefined,{maximumFractionDigits:0})}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {visits.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-md p-10 text-center" data-testid="np-visits-empty">
          <CalendarClock className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
          <p className="text-sm text-[#8A847A] mt-3">No visits in this date range.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-[#F7F5F0]">
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Date</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">CPT</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Duration</th>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Notes</th>
            </tr></thead>
            <tbody>{visits.map((v) => (
              <tr key={v.id} className="border-t border-[#E5E0D8]" data-testid={`np-visit-row-${v.id}`}>
                <td className="px-4 py-2 text-sm font-mono">{v.visit_date}</td>
                <td className="px-4 py-2 text-sm">
                  {v.facility_id ? <Link to={`/facilities/${v.facility_id}`} className="hover:text-[#4A6B5D]">{v.facility_name || "—"}</Link> : (v.facility_name || "—")}
                </td>
                <td className="px-4 py-2 text-sm">
                  {(v.cpt_codes || []).length === 0 ? <span className="text-[#8A847A]">—</span> : (
                    <div className="flex flex-wrap gap-1">
                      {v.cpt_codes.map((c, i) => (
                        <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded border border-[#B7CDBB] bg-[#E9F0EA] text-[#3B5C46] text-[11px] font-mono">
                          {c.code}{c.units > 1 ? `×${c.units}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-right font-mono">{v.duration_min ? `${v.duration_min}m` : "—"}</td>
                <td className="px-4 py-2 text-sm text-[#5C5850] truncate max-w-[240px]">{v.notes || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CredentialingTable({ facilities, onSetApproval }) {  const total = facilities.length;
  const pending = facilities.filter((f) => f.np_approval_status === "pending").length;
  const approved = facilities.filter((f) => f.np_approval_status === "approved").length;

  if (total === 0) {
    return (
      <div className="bg-white border border-[#E5E0D8] rounded-md p-10 text-center" data-testid="np-credentialing-empty">
        <ShieldCheck className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
        <p className="text-sm text-[#8A847A] mt-3">No facilities assigned yet.</p>
        <p className="text-xs text-[#8A847A] mt-1">Assign a facility first, then mark credentialing here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="np-credentialing-tab">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-[#E5E0D8] rounded-md p-3 text-center">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">Assigned</div>
          <div className="font-mono font-semibold text-[#292723] mt-1">{total}</div>
        </div>
        <div className="bg-[#FBF3E1] border border-[#E1D0AA] rounded-md p-3 text-center" data-testid="np-cred-pending-count">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[#6B4E1E]">Credentials pending</div>
          <div className="font-mono font-semibold text-[#6B4E1E] mt-1">{pending}</div>
        </div>
        <div className="bg-[#E9F0EA] border border-[#B7CDBB] rounded-md p-3 text-center" data-testid="np-cred-approved-count">
          <div className="text-[10px] uppercase tracking-[0.08em] text-[#3B5C46]">Credentials approved</div>
          <div className="font-mono font-semibold text-[#3B5C46] mt-1">{approved}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
        <table className="w-full">
          <thead><tr className="bg-[#F7F5F0]">
            <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
            <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Status</th>
          </tr></thead>
          <tbody>{facilities.map((f) => {
            const isApproved = f.np_approval_status === "approved";
            const isPending = f.np_approval_status === "pending";
            return (
              <tr key={f.id} className="border-t border-[#E5E0D8]" data-testid={`np-cred-row-${f.id}`}>
                <td className="px-4 py-3">
                  <Link to={`/facilities/${f.id}`} className="font-medium text-[#292723] hover:text-[#4A6B5D]">{f.name}</Link>
                  <div className="text-xs text-[#8A847A]">{[f.city, f.state].filter(Boolean).join(", ") || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onSetApproval(f.id, "pending")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                        isPending
                          ? "bg-[#FBF3E1] border-[#E1D0AA] text-[#6B4E1E] font-medium"
                          : "bg-white border-[#E5E0D8] text-[#8A847A] hover:bg-[#F7F5F0]"
                      }`}
                      data-testid={`np-cred-pending-btn-${f.id}`}
                      aria-pressed={isPending}
                    >
                      Pending
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetApproval(f.id, "approved")}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                        isApproved
                          ? "bg-[#E9F0EA] border-[#B7CDBB] text-[#3B5C46] font-medium"
                          : "bg-white border-[#E5E0D8] text-[#8A847A] hover:bg-[#F7F5F0]"
                      }`}
                      data-testid={`np-cred-approved-btn-${f.id}`}
                      aria-pressed={isApproved}
                    >
                      <Check className="h-3 w-3 inline mr-1"/> Approved
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
}

export default function NPDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [np, setNp] = useState(null);
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);

  // Contact edit
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "", address: "", avatar_url: "" });
  const [savingContact, setSavingContact] = useState(false);

  // Facility assign / create
  const [facSheetOpen, setFacSheetOpen] = useState(false);
  const [facMode, setFacMode] = useState("assign"); // 'assign' | 'create'
  const [allFacilities, setAllFacilities] = useState([]);
  const [facPicks, setFacPicks] = useState(new Set());
  const [facQuery, setFacQuery] = useState("");
  const [facForm, setFacForm] = useState({ name: "", city: "", state: "", patient_count: 0, visits_per_month: 0, visits_per_week: 0 });
  const [savingFac, setSavingFac] = useState(false);

  // Bulk unassign on the assigned-facilities table
  const [assignedPicks, setAssignedPicks] = useState(new Set());

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/nps/${id}`); setNp(data); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[np-detail] load failed:", e?.message || e);
      toast.error("Could not load NP");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!np) return <div className="p-10 text-[#8A847A]">Loading…</div>;

  const remove = async () => {
    if (!window.confirm(`Delete ${np.name}?`)) return;
    try { await api.delete(`/nps/${id}`); toast.success("Removed"); nav("/nps"); }
    catch { toast.error("Delete failed"); }
  };

  // ---------- Notes ----------
  const addNote = async () => {
    const body = draft.trim();
    if (!body) return;
    setAdding(true);
    try { await api.post(`/nps/${id}/notes`, { body }); setDraft(""); await load(); toast.success("Note added"); }
    catch { toast.error("Could not add note"); }
    finally { setAdding(false); }
  };
  const editNote = async (noteId, body) => {
    try { await api.put(`/nps/${id}/notes/${noteId}`, { body }); await load(); toast.success("Note updated"); }
    catch { toast.error("Could not update"); }
  };
  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/nps/${id}/notes/${noteId}`); await load(); toast.success("Note deleted"); }
    catch { toast.error("Could not delete"); }
  };

  // ---------- Contact edit ----------
  const openContact = () => {
    setContactForm({
      name: np.name || "",
      phone: np.phone || "",
      email: np.email || "",
      address: np.address || "",
      city: np.city || "",
      state: np.state || "",
      zip: np.zip || "",
      avatar_url: np.avatar_url || "",
    });
    setContactOpen(true);
  };
  const saveContact = async (e) => {
    e.preventDefault();
    setSavingContact(true);
    try {
      await api.put(`/nps/${id}`, contactForm);
      toast.success("Contact updated");
      setContactOpen(false);
      await load();
    } catch { toast.error("Could not save"); }
    finally { setSavingContact(false); }
  };

  // ---------- Facility assign / create ----------
  const openFacSheet = async (mode) => {
    let facs = [];
    try {
      const { data } = await api.get("/facilities");
      facs = data || [];
      setAllFacilities(facs);
    } catch { toast.error("Could not load facilities"); }
    const hasUnassigned = facs.some((f) => f.np_id !== id);
    const initial = mode || (hasUnassigned ? "assign" : "create");
    setFacMode(initial);
    setFacPicks(new Set());
    setFacQuery("");
    setFacForm({ name: "", city: "", state: "", patient_count: 0, visits_per_month: 0, visits_per_week: 0 });
    setFacSheetOpen(true);
  };
  const togglePick = (facId) => {
    setFacPicks((prev) => {
      const next = new Set(prev);
      if (next.has(facId)) next.delete(facId); else next.add(facId);
      return next;
    });
  };
  const assignFacilities = async (e) => {
    e.preventDefault();
    if (facPicks.size === 0) return;
    setSavingFac(true);
    try {
      const { data } = await api.post(`/nps/${id}/assign-facilities`, { facility_ids: Array.from(facPicks) });
      toast.success(`Assigned ${data?.assigned ?? facPicks.size} ${facPicks.size === 1 ? "facility" : "facilities"}`);
      setFacSheetOpen(false);
      await load();
    } catch { toast.error("Could not assign"); }
    finally { setSavingFac(false); }
  };
  const createFacility = async (e) => {
    e.preventDefault();
    if (!facForm.name.trim()) return;
    setSavingFac(true);
    try {
      await api.post("/facilities", {
        name: facForm.name.trim(),
        city: facForm.city.trim(),
        state: facForm.state.trim(),
        patient_count: Number(facForm.patient_count) || 0,
        visits_per_month: Number(facForm.visits_per_month) || 0,
        visits_per_week: Number(facForm.visits_per_week) || 0,
        np_id: id,
      });
      toast.success("Facility created and assigned");
      setFacSheetOpen(false);
      await load();
    } catch { toast.error("Could not create"); }
    finally { setSavingFac(false); }
  };
  const unassignFacility = async (facId, facName) => {
    if (!window.confirm(`Remove ${facName} from ${np.name}?`)) return;
    try {
      await api.put(`/facilities/${facId}`, { np_id: null });
      toast.success("Unassigned");
      await load();
    } catch { toast.error("Could not unassign"); }
  };

  const toggleAssignedPick = (facId) => {
    setAssignedPicks((prev) => {
      const next = new Set(prev);
      if (next.has(facId)) next.delete(facId); else next.add(facId);
      return next;
    });
  };
  const unassignSelected = async () => {
    if (assignedPicks.size === 0) return;
    if (!window.confirm(`Remove ${assignedPicks.size} ${assignedPicks.size === 1 ? "facility" : "facilities"} from ${np.name}?`)) return;
    try {
      await Promise.all(Array.from(assignedPicks).map((fid) => api.put(`/facilities/${fid}`, { np_id: null })));
      toast.success(`Removed ${assignedPicks.size}`);
      setAssignedPicks(new Set());
      await load();
    } catch { toast.error("Some removals failed"); }
  };

  // ---------- Credentialing ----------
  const setApproval = async (facId, status) => {
    try {
      await api.put(`/facilities/${facId}`, { np_approval_status: status });
      await load();
    } catch { toast.error("Could not update"); }
  };

  const notes = [...(np.note_entries || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const facilitiesForAssign = allFacilities
    .filter((f) => f.np_id !== id)
    .filter((f) => {
      const s = facQuery.trim().toLowerCase();
      if (!s) return true;
      return (f.name || "").toLowerCase().includes(s)
        || (f.city || "").toLowerCase().includes(s)
        || (f.state || "").toLowerCase().includes(s);
    });

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <Link to="/nps" className="inline-flex items-center gap-1 text-sm text-[#5C5850] hover:text-[#4A6B5D]">
        <ArrowLeft className="h-4 w-4" /> All practitioners
      </Link>

      <div className="mt-4 grid lg:grid-cols-3 gap-6">
        {/* Left card */}
        <div className="bg-white border border-[#E5E0D8] rounded-md p-6 lg:col-span-1">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={np.avatar_url} alt={np.name} />
              <AvatarFallback className="bg-[#F0EBE1] text-[#4A6B5D] font-semibold text-lg">{initials(np.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.06em] text-[#8A847A]">Nurse Practitioner</div>
              <h1 className="heading text-2xl font-bold text-[#292723] mt-1 leading-tight">{np.name}</h1>
              <div className="text-xs text-[#8A847A] mt-1">Endocrinology · Dialog Healthcare</div>
            </div>
            <Button
              variant="ghost" size="sm" onClick={openContact}
              className="text-[#4A6B5D] hover:bg-[#F0EBE1] flex-shrink-0"
              data-testid="np-edit-contact-btn"
              aria-label="Edit contact"
            >
              <UserRoundPen className="h-4 w-4"/>
            </Button>
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <div className="flex gap-3">
              <Phone className="h-4 w-4 text-[#8A847A] mt-0.5 flex-shrink-0"/>
              {np.phone
                ? <a href={`tel:${np.phone.replace(/[^0-9+]/g, "")}`} className="text-[#292723] hover:text-[#4A6B5D] hover:underline" data-testid="np-phone">{np.phone}</a>
                : <span className="italic text-[#8A847A]" data-testid="np-phone">Add phone number</span>}
            </div>
            <div className="flex gap-3">
              <Mail className="h-4 w-4 text-[#8A847A] mt-0.5 flex-shrink-0"/>
              {np.email
                ? <a href={`mailto:${np.email}`} className="break-all text-[#292723] hover:text-[#4A6B5D] hover:underline" data-testid="np-email">{np.email}</a>
                : <span className="italic text-[#8A847A]" data-testid="np-email">Add email</span>}
            </div>
            <div className="flex gap-3">
              <MapPin className="h-4 w-4 text-[#8A847A] mt-0.5 flex-shrink-0"/>
              {np.address
                ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(np.address)}`} target="_blank" rel="noreferrer" className="text-[#292723] hover:text-[#4A6B5D] hover:underline" data-testid="np-address">{np.address}</a>
                : <span className="italic text-[#8A847A]" data-testid="np-address">Add address</span>}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-2 pt-6 border-t border-[#E5E0D8] text-center">
            {[
              ["Fac.", np.facility_count],
              ["Pts", np.patient_count],
              ["Vis 30d", np.visits_last_30d ?? np.visits_per_month],
              ["Vis 7d", np.visits_last_7d ?? np.visits_per_week],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-[0.08em] text-[#8A847A]">{k}</div>
                <div className="font-mono font-semibold text-[#292723] mt-0.5">{v ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-start gap-1.5 text-[10px] text-[#8A847A] leading-snug">
            <Info className="h-3 w-3 mt-0.5 flex-shrink-0"/>
            <span>Patients + visits are live from synced DrChrono data. Facilities roll up manual counts if no sync data exists.</span>
          </div>

          <Button variant="outline" onClick={remove} className="mt-4 w-full border-[#E5E0D8] text-[#C17D6E]" data-testid="np-remove-btn">
            <Trash2 className="h-4 w-4 mr-2"/>Remove NP
          </Button>
        </div>

        {/* Right column */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs defaultValue="facilities">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850]">Coverage</div>
                <TabsList className="mt-1 bg-white border border-[#E5E0D8]">
                  <TabsTrigger value="facilities" data-testid="np-tab-facilities">
                    <Building2 className="h-3.5 w-3.5 mr-1.5"/> Facilities ({(np.facilities || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="visits" data-testid="np-tab-visits">
                    <CalendarClock className="h-3.5 w-3.5 mr-1.5"/> Visits
                  </TabsTrigger>
                  <TabsTrigger value="credentialing" data-testid="np-tab-credentialing">
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5"/> Credentialing
                  </TabsTrigger>
                </TabsList>
              </div>
              <div className="flex gap-2">
                {assignedPicks.size > 0 && (
                  <Button
                    variant="outline"
                    onClick={unassignSelected}
                    className="border-[#E7C9C1] text-[#8A3E30] hover:bg-[#F7E9E5]"
                    data-testid="np-bulk-unassign-btn"
                  >
                    <UserRoundMinus className="h-4 w-4 mr-2"/> Remove {assignedPicks.size}
                  </Button>
                )}
                <Button onClick={() => openFacSheet()} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="np-add-facility-btn">
                  <Plus className="h-4 w-4 mr-2"/> Add Facility
                </Button>
              </div>
            </div>

            <TabsContent value="facilities" className="mt-4">
              {(np.facilities || []).length === 0 ? (
                <div className="bg-white border border-[#E5E0D8] rounded-md p-10 text-center">
                  <Building2 className="h-8 w-8 mx-auto text-[#8A847A] opacity-60"/>
                  <p className="text-sm text-[#8A847A] mt-3">No facilities assigned yet.</p>
                  <p className="text-xs text-[#8A847A] mt-1">Tap "Add Facility" above to assign an existing one or create a new one.</p>
                </div>
              ) : (
                <div className="bg-white border border-[#E5E0D8] rounded-md overflow-hidden">
                  <table className="w-full">
                    <thead><tr className="bg-[#F7F5F0]">
                      <th className="w-8 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={assignedPicks.size > 0 && assignedPicks.size === np.facilities.length}
                          onChange={(e) => setAssignedPicks(e.target.checked ? new Set(np.facilities.map((f) => f.id)) : new Set())}
                          className="h-4 w-4 accent-[#4A6B5D] cursor-pointer"
                          data-testid="np-assigned-select-all"
                          aria-label="Select all"
                        />
                      </th>
                      <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
                      <th className="text-left px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">MD Approval</th>
                      <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Patients</th>
                      <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Vis 30d</th>
                      <th className="text-right px-4 py-2 text-xs uppercase tracking-[0.05em] text-[#5C5850]">Vis 7d</th>
                      <th className="w-8"></th>
                    </tr></thead>
                    <tbody>{np.facilities.map((f) => (
                      <tr key={f.id} className={`border-t border-[#E5E0D8] table-row-hover ${assignedPicks.has(f.id) ? "bg-[#F0EBE1]" : ""}`} data-testid={`np-fac-row-${f.id}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={assignedPicks.has(f.id)}
                            onChange={() => toggleAssignedPick(f.id)}
                            className="h-4 w-4 accent-[#4A6B5D] cursor-pointer"
                            data-testid={`np-assigned-check-${f.id}`}
                            aria-label={`Select ${f.name}`}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <Link to={`/facilities/${f.id}`} className="font-medium text-[#292723] hover:text-[#4A6B5D]">{f.name}</Link>
                          <div className="text-xs text-[#8A847A]">{[f.city, f.state].filter(Boolean).join(", ") || "—"}</div>
                        </td>
                        <td className="px-4 py-2"><StatusPill status={APPROVAL_TONE[f.np_approval_status] || "gray"} label={APPROVAL_LABELS[f.np_approval_status] || "—"} /></td>
                        <td className="px-4 py-2 text-sm text-right font-mono">{f.patients_synced ?? f.patient_count ?? 0}</td>
                        <td className="px-4 py-2 text-sm text-right font-mono">{f.visits_last_30d ?? f.visits_per_month ?? 0}</td>
                        <td className="px-4 py-2 text-sm text-right font-mono">{f.visits_last_7d ?? f.visits_per_week ?? 0}</td>
                        <td className="px-2 py-2 text-right">
                          <button
                            onClick={() => unassignFacility(f.id, f.name)}
                            className="text-[#8A847A] hover:text-[#C17D6E] p-1"
                            aria-label="Unassign facility"
                            data-testid={`np-fac-unassign-${f.id}`}
                            type="button"
                          >
                            <UserRoundMinus className="h-3.5 w-3.5"/>
                          </button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="credentialing" className="mt-4">
              <CredentialingTable
                facilities={np.facilities || []}
                onSetApproval={setApproval}
              />
            </TabsContent>

            <TabsContent value="visits" className="mt-4">
              <NPVisits npId={id} />
            </TabsContent>
          </Tabs>

          {(np.open_issues || []).length > 0 && (
            <div className="bg-white border border-[#E5E0D8] rounded-md p-5">
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2"><AlertOctagon className="h-3.5 w-3.5"/> Open issues</div>
              <div className="mt-3 divide-y divide-[#E5E0D8]">
                {np.open_issues.map((i) => (
                  <div key={i.id} className="py-2 flex items-center justify-between">
                    <div className="text-sm">{i.title}</div>
                    <StatusPill status={i.severity === "critical" ? "red" : i.severity === "high" ? "orange" : "gray"} label={i.severity} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {(np.upcoming_meetings || []).length > 0 && (
            <div className="bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="np-meetings-card">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
                  <CalendarClock className="h-3.5 w-3.5"/> Upcoming meetings ({np.upcoming_meetings.length})
                </div>
                <Link to="/meetings" className="text-xs text-[#4A6B5D] hover:underline">All →</Link>
              </div>
              <div className="mt-3 divide-y divide-[#E5E0D8]">
                {np.upcoming_meetings.map((m) => (
                  <div key={m.id} className="py-2">
                    <div className="text-sm text-[#292723]">{m.title}</div>
                    <div className="text-xs text-[#8A847A] mt-0.5">
                      {(() => { try { return new Date(m.when).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return m.when; } })()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes card */}
          <div className="bg-white border border-[#E5E0D8] rounded-md p-5" data-testid="np-notes-card">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-[0.06em] text-[#5C5850] flex items-center gap-2">
                <StickyNote className="h-3.5 w-3.5"/> Notes
              </div>
              <div className="text-[11px] text-[#8A847A]">{notes.length} {notes.length === 1 ? "note" : "notes"}</div>
            </div>

            <div className="mt-3 space-y-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Write a note — schedule quirks, calls to make, credential deadlines, ongoing conversations…"
                rows={3}
                className="bg-white border-[#E5E0D8]"
                data-testid="np-notes-textarea"
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); addNote(); }
                }}
              />
              <div className="flex justify-between items-center">
                <div className="text-[10px] text-[#8A847A]">Tip: ⌘/Ctrl + Enter to add</div>
                <Button onClick={addNote} disabled={!draft.trim() || adding} className="bg-[#4A6B5D] hover:bg-[#3E594D] disabled:opacity-50" data-testid="np-notes-add-btn">
                  <Plus className="h-4 w-4 mr-1"/> {adding ? "Adding…" : "Add note"}
                </Button>
              </div>
            </div>

            {notes.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="np-notes-list">
                {notes.map((n) => <NoteCard key={n.id} note={n} onDelete={deleteNote} onSaveEdit={editNote} />)}
              </div>
            )}
          </div>

          <NpDocuments npId={id} />
        </div>
      </div>

      {/* Edit contact sheet */}
      <Sheet open={contactOpen} onOpenChange={setContactOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">Edit contact</SheetTitle></SheetHeader>
          <form onSubmit={saveContact} className="mt-6 space-y-4">
            <FieldRow label="Name">
              <Input required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-contact-name"/>
            </FieldRow>
            <FieldRow label="Phone">
              <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" placeholder="(555) 123-4567" data-testid="np-contact-phone"/>
            </FieldRow>
            <FieldRow label="Email">
              <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" placeholder="name@example.com" data-testid="np-contact-email"/>
            </FieldRow>
            <FieldRow label="Address">
              <AddressAutocomplete
                value={contactForm.address}
                onChange={(v) => setContactForm({ ...contactForm, address: v })}
                onSelect={(s) => {
                  setContactForm((f) => ({
                    ...f,
                    address: s.street || s.label,
                    city: s.city || f.city,
                    state: s.state || f.state,
                    zip: s.postcode || f.zip,
                  }));
                }}
                testId="np-contact-address"
                placeholder="Start typing an address…"
              />
            </FieldRow>
            <div className="grid grid-cols-3 gap-3">
              <FieldRow label="City">
                <Input value={contactForm.city || ""} onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-contact-city"/>
              </FieldRow>
              <FieldRow label="State">
                <Input value={contactForm.state || ""} onChange={(e) => setContactForm({ ...contactForm, state: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-contact-state"/>
              </FieldRow>
              <FieldRow label="Zip">
                <Input value={contactForm.zip || ""} onChange={(e) => setContactForm({ ...contactForm, zip: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-contact-zip"/>
              </FieldRow>
            </div>
            <FieldRow label="Avatar URL (optional)">
              <Input value={contactForm.avatar_url} onChange={(e) => setContactForm({ ...contactForm, avatar_url: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" placeholder="https://…" data-testid="np-contact-avatar"/>
            </FieldRow>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setContactOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingContact} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="np-contact-save-btn">
                {savingContact ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Facility assign / create sheet */}
      <Sheet open={facSheetOpen} onOpenChange={setFacSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle className="heading text-2xl">Add facility to {np.name}</SheetTitle></SheetHeader>
          <div className="mt-4 inline-flex rounded-md border border-[#E5E0D8] p-0.5 bg-[#F7F5F0]">
            <button
              type="button"
              onClick={() => setFacMode("assign")}
              className={`px-3 py-1.5 text-xs rounded ${facMode === "assign" ? "bg-white text-[#292723] shadow-sm" : "text-[#5C5850]"}`}
              data-testid="np-fac-tab-assign"
            >Assign existing</button>
            <button
              type="button"
              onClick={() => setFacMode("create")}
              className={`px-3 py-1.5 text-xs rounded ${facMode === "create" ? "bg-white text-[#292723] shadow-sm" : "text-[#5C5850]"}`}
              data-testid="np-fac-tab-create"
            >Create new</button>
          </div>

          {facMode === "assign" ? (
            <form onSubmit={assignFacilities} className="mt-6 space-y-4">
              {facilitiesForAssign.length === 0 && allFacilities.filter((f) => f.np_id !== id).length === 0 ? (
                <div className="text-sm text-[#8A847A]">No unassigned facilities — switch to "Create new" tab.</div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847A]" />
                    <Input
                      value={facQuery}
                      onChange={(e) => setFacQuery(e.target.value)}
                      placeholder="Search by name, city, or state"
                      className="pl-9 h-10 bg-white border-[#E5E0D8]"
                      data-testid="np-fac-search"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-[#5C5850]">
                    <div>
                      <span className="font-semibold text-[#292723]">{facPicks.size}</span> selected
                      <span className="text-[#8A847A]"> · {facilitiesForAssign.length} shown</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setFacPicks(new Set(facilitiesForAssign.map((f) => f.id)))}
                        className="text-[#4A6B5D] hover:underline disabled:opacity-40"
                        disabled={facilitiesForAssign.length === 0}
                        data-testid="np-fac-select-all"
                      >Select all</button>
                      <button
                        type="button"
                        onClick={() => setFacPicks(new Set())}
                        className="text-[#8A847A] hover:underline disabled:opacity-40"
                        disabled={facPicks.size === 0}
                        data-testid="np-fac-clear"
                      >Clear</button>
                    </div>
                  </div>
                  <div className="border border-[#E5E0D8] rounded-md overflow-hidden max-h-[420px] overflow-y-auto" data-testid="np-fac-multi-list">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F7F5F0] sticky top-0">
                        <tr>
                          <th className="w-8 px-3 py-2"></th>
                          <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Facility</th>
                          <th className="text-left px-2 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Location</th>
                          <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.05em] text-[#5C5850]">Pts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facilitiesForAssign.length === 0 ? (
                          <tr><td colSpan={4} className="px-3 py-6 text-center text-[#8A847A]">No matches.</td></tr>
                        ) : facilitiesForAssign.map((f) => {
                          const checked = facPicks.has(f.id);
                          return (
                            <tr
                              key={f.id}
                              onClick={() => togglePick(f.id)}
                              className={`border-t border-[#E5E0D8] cursor-pointer ${checked ? "bg-[#F0EBE1]" : "hover:bg-[#FAF8F3]"}`}
                              data-testid={`np-fac-multi-row-${f.id}`}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => togglePick(f.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 accent-[#4A6B5D] cursor-pointer"
                                  data-testid={`np-fac-multi-check-${f.id}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium text-[#292723]">{f.name}</div>
                                {f.np_id && <div className="text-[10px] text-[#B08A55]">Currently on another NP — will be moved here</div>}
                              </td>
                              <td className="px-2 py-2 text-xs text-[#5C5850]">{[f.city, f.state].filter(Boolean).join(", ") || "—"}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs text-[#5C5850]">{f.patient_count || 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="text-[11px] text-[#8A847A]">Selecting a facility currently assigned to another NP will move it here.</div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setFacSheetOpen(false)}>Cancel</Button>
                <Button
                  type="submit"
                  disabled={facPicks.size === 0 || savingFac}
                  className="bg-[#4A6B5D] hover:bg-[#3E594D]"
                  data-testid="np-fac-assign-save-btn"
                >
                  {savingFac ? "Assigning…" : `Assign ${facPicks.size || ""}`.trim()}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={createFacility} className="mt-6 space-y-4">
              <FieldRow label="Facility name">
                <Input required value={facForm.name} onChange={(e) => setFacForm({ ...facForm, name: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-fac-name"/>
              </FieldRow>
              <div className="grid grid-cols-2 gap-3">
                <FieldRow label="City">
                  <Input value={facForm.city} onChange={(e) => setFacForm({ ...facForm, city: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-fac-city"/>
                </FieldRow>
                <FieldRow label="State">
                  <Input value={facForm.state} onChange={(e) => setFacForm({ ...facForm, state: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-fac-state"/>
                </FieldRow>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <FieldRow label="Patients">
                  <Input type="number" min="0" value={facForm.patient_count} onChange={(e) => setFacForm({ ...facForm, patient_count: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-fac-patients"/>
                </FieldRow>
                <FieldRow label="Visits / mo">
                  <Input type="number" min="0" value={facForm.visits_per_month} onChange={(e) => setFacForm({ ...facForm, visits_per_month: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-fac-vpm"/>
                </FieldRow>
                <FieldRow label="Visits / wk">
                  <Input type="number" min="0" value={facForm.visits_per_week} onChange={(e) => setFacForm({ ...facForm, visits_per_week: e.target.value })} className="h-10 bg-white border-[#E5E0D8]" data-testid="np-fac-vpw"/>
                </FieldRow>
              </div>
              <div className="text-[11px] text-[#8A847A]">More detail (address, credentials, follow-ups, MD approval) can be added later from the facility page.</div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setFacSheetOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={!facForm.name.trim() || savingFac} className="bg-[#4A6B5D] hover:bg-[#3E594D]" data-testid="np-fac-create-save-btn">
                  {savingFac ? "Creating…" : "Create & assign"}
                </Button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
